import { createElement, useEffect, useRef, type ComponentProps } from 'react'
import * as EChartsCoreModule from 'echarts-for-react/esm/core'

// `echarts-for-react` ships its tree-shakeable "core" build twice: a CommonJS `lib/core` and a
// real-ESM `esm/core`. The package has no "exports" map, so a deep `import X from
// 'echarts-for-react/lib/core'` resolves to the raw CJS file — and Vite 8 / Rolldown's CJS interop
// then exposes the default export DOUBLE-wrapped as `{ default: { default: Component } }`. React is
// handed that object and throws "Element type is invalid ... but got: object", which the error
// boundary turns into a full white-screen of the visualization workspace — only in the production
// build, never under the dev/test transform (esbuild), which double-wraps differently.
// (Sentry: SPCFY-FRONTEND-HZ / -J0.)
//
// Importing the ESM entry (`esm/core`) avoids the CJS wrapping entirely. We additionally peel any
// residual `default` nesting so the value handed to React is always the component class regardless
// of how a given bundler interops the module. See EChartsCore.test.tsx.

/** Peel nested `{ default }` wrappers until reaching the underlying (non-object) value. */
export function resolveComponentDefault(mod: unknown): unknown {
  let current = mod
  while (current != null && typeof current === 'object' && 'default' in current) {
    current = (current as { default: unknown }).default
  }
  return current
}

type EChartsCoreComponent = (typeof EChartsCoreModule)['default']

const EChartsReactCore = resolveComponentDefault(EChartsCoreModule) as EChartsCoreComponent

/**
 * echarts-for-react ignores the first size change it observes, to spare intro animations. In the
 * workspace grid that first change is the grid widening the card from its initial width, so the
 * chart stayed drawn at the narrower size. Every size change is passed on here instead.
 */
function ReactEChartsCore(props: ComponentProps<EChartsCoreComponent>) {
  const chartRef = useRef<InstanceType<EChartsCoreComponent>>(null)

  useEffect(() => {
    const element = chartRef.current?.ele
    if (!element || typeof ResizeObserver === 'undefined') return
    let frame: number | null = null
    // Resizing inside the observer callback changes layout in the same frame, which the browser reports as a
    // "ResizeObserver loop" error; the next frame is soon enough. A bare resize() keeps the size the chart was
    // created with, so 'auto' re-measures the container.
    const observer = new ResizeObserver(() => {
      if (frame !== null) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = null
        const chart = chartRef.current?.getEchartsInstance()
        if (chart && (chart.getWidth() !== element.clientWidth || chart.getHeight() !== element.clientHeight)) {
          chart.resize({ width: 'auto', height: 'auto' })
        }
      })
    })
    observer.observe(element)
    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  return createElement(EChartsReactCore, { ...props, ref: chartRef })
}

export default ReactEChartsCore
